import { FREQUENCY_TYPE } from '../constant/syllabus.constant';
import { tr } from '../i18n/i18n';
import { USER_TYPE } from '../constants';

export const generateUniqueID = (): string => {
  const randomPart = () => Math.random().toString(16).slice(2, 10);
  return `${randomPart()}-${Date.now()}`;
};

export const renderStatus = (rowData: boolean | null | undefined) => (
  <div
    className={`flex gap-2 ms-auto justify-end rtl:justify-start ${getStatusColor(rowData)} font-poppins-regular`}
  >
    <span>{getValue(rowData)}</span>
  </div>
);

const getStatusColor = (status: boolean | null | undefined): string => {
  switch (status) {
    case true:
      return 'text-green-600'; // Active color
    case false:
      return 'text-purple-600'; // Inactive color
    default:
      return 'text-gray-500'; // Default color for undefined or null
  }
};

export const truncateFileName = (name: string, maxLength = 30): string => {
  if (name.length <= maxLength) return name;

  const extIndex = name.lastIndexOf('.');
  const extension = extIndex !== -1 ? name.slice(extIndex) : '';
  const base = extIndex !== -1 ? name.slice(0, extIndex) : name;

  const frontChars = Math.ceil((maxLength - extension.length - 3) / 2);
  const backChars = Math.floor((maxLength - extension.length - 3) / 2);

  return `${base.slice(0, frontChars)}...${base.slice(-backChars)}${extension}`;
};

export function isHtmlEmpty(html: string): boolean {
  if (!html) return true;
  // Remove all tags and check if anything remains
  const text = html
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, '')
    .replace(/\s+/g, '')
    .trim();
  if (text.length > 0) return false;
  // Check for common empty HTML patterns
  return /^((<p><br\s*\/?><\/p>|\s|<br\s*\/?\s*>)*)$/i.test(html.trim());
}

const getValue = (status: boolean | null | undefined): string => {
  switch (status) {
    case true:
      return tr('common.status_active', 'Active');
    case false:
      return tr('common.status_inactive', 'Inactive');
    default:
      return tr('common.status_unknown', 'Unknown');
  }
};

export function getDayAbbreviation(day: string): string {
  const daysMap: Record<string, string> = {
    Monday: 'MO',
    Tuesday: 'TU',
    Wednesday: 'WE',
    Thursday: 'TH',
    Friday: 'FR',
    Saturday: 'SA',
    Sunday: 'SU',
  };
  return daysMap[day] || '';
}

export function formatTimestamp(timestamp: string): string {
  const date = new Date(timestamp);
  return date.toISOString().slice(0, 16);
}

export const convertToNumber = (isoString: string): number => {
  const [hours, minutes] = isoString.split(':').map(Number);
  const timeAsDecimal = hours + minutes / 60;
  return timeAsDecimal;
};

export const validatePhone = (phone: string): string => {
  if (!phone) return tr('g.field_is_required', 'Field is required');

  return '';
};

export const convertMinuteToMiliseconds = (minutes: number): number => {
  return minutes * 60 * 1000;
};

export function extractLatLngFromGoogleMapsUrl(
  url: string,
): { latitude: string; longitude: string } | null {
  // Example: https://www.google.com/maps?q=6.9271,79.8612
  // Support '?q=lat,lng' and '/@lat,lng' formats
  let match = url.match(/maps\?q=([\d.-]+),([\d.-]+)/);
  if (match) {
    return { latitude: match[1], longitude: match[2] };
  }
  match = url.match(/@([\d.-]+),([\d.-]+)[,?]/);
  if (match) {
    return { latitude: match[1], longitude: match[2] };
  }
  return null;
}

export function getFrequencyTypeLabel(typeValue: FREQUENCY_TYPE | string | undefined): string {
  if (typeValue === FREQUENCY_TYPE.DAILY) return 'Day';
  if (typeValue === FREQUENCY_TYPE.WEEKLY) return 'Week';
  if (typeValue === FREQUENCY_TYPE.MONTHLY) return 'Month';
  if (typeValue === FREQUENCY_TYPE.QUARTERLY) return 'Quarter';
  if (typeValue === FREQUENCY_TYPE.SEMI) return 'Semi-Annual';
  if (typeValue === FREQUENCY_TYPE.ANNUAL) return 'Year';
  return 'Type';
}

/**
 * Convert base64 data URL string to File object for S3 upload
 * @param base64String - Base64 data URL (e.g., "data:image/png;base64,iVBORw0KGgo...")
 * @param fileName - Name for the file (e.g., "signature_1234567890.png")
 * @returns File object ready for FormData upload
 */
export const base64ToFile = (base64String: string, fileName: string): File => {
  const arr = base64String.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  const n = bstr.length;
  const u8arr = new Uint8Array(n);
  for (let i = 0; i < n; i++) {
    u8arr[i] = bstr.charCodeAt(i);
  }
  return new File([u8arr], fileName, { type: mime });
};

/**
 * Format a Date object to DD.MM.YYYY format
 * @param date - Date object to format
 * @returns Formatted date string (e.g., "26.02.2026")
 */
export const formatDDMMYYYY = (date: Date): string => {
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const y = date.getFullYear().toString();
  return `${d}.${m}.${y}`;
};
